import { karachiToday } from "../outreachDates";
import { RfpOutreachTimelines } from "./OutreachTimeline";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Landmark,
  ExternalLink,
  Rocket,
  Send,
  StickyNote,
  Loader2,
  Users,
} from "lucide-react";
import type { CrmOpportunityDetail, CrmActivity, DeleteOpportunityResult, DeletePreview } from "../api/CrmApi";
import { getDeletePreview } from "../api/CrmApi";
import { useCrm } from "../modal/CrmContext";
import { VerifyContext } from "../../../app/VerifyContext";
import { StageBadge, DeadlineChip, formatDateTime, ACTIVITY_LABELS } from "./crmUi";
import GatesSection from "./GatesSection";
import ConfirmModal from "../../../shared/ConfirmModal";
import Modal from "../../../shared/Modal";
import Button from "../../../shared/Button";
import { TONE_SOFT } from "../../../shared/tones";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "../../../shared/tableStyles";

interface Props {
  opportunityId: number;
  onClose: () => void;
}

function ActivityRow({ activity }: { activity: CrmActivity }) {
  const label = ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type;
  return (
    <li className="relative pl-5 pb-4 last:pb-0 text-xs">
      {/* timeline dot on the rail */}
      <span className="absolute left-0 top-1 w-2 h-2 rounded-full bg-primary-border ring-4 ring-surface" />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-foreground font-medium">{label}</span>
        {activity.activity_type === "stage_change" && (
          <span className="text-muted-foreground">
            {activity.from_stage_key ?? "start"} → {activity.to_stage_key}
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{activity.actor_type}</span>
        <span className="ml-auto text-muted-foreground whitespace-nowrap">{formatDateTime(activity.created_at)}</span>
      </div>
      {activity.notes && <p className="text-muted-foreground mt-0.5 break-words">{activity.notes}</p>}
    </li>
  );
}

function FactRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground text-right min-w-0 break-words">{children}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-medium text-muted-foreground mb-2">{children}</p>;
}

export default function OpportunityDetailModal({ opportunityId, onClose }: Props) {
  const { superAdmin } = useContext(VerifyContext);
  const {
    pipeline, pipelineLoaded, loadDetail, changeStage, addNote, recordRfpEvent, deleteOpportunity,
  } = useCrm();
  const location = useLocation();
  const primeContactsBasePath = location.pathname.startsWith("/employee")
    ? "/employee/crm/prime-contacts"
    : "/admin/crm/prime-contacts";

  const [detail, setDetail] = useState<CrmOpportunityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [startedOn, setStartedOn] = useState("");   // the day Start was pressed in SmartLead; blank = today
  const [stageNotes, setStageNotes] = useState("");
  const [pendingStage, setPendingStage] = useState<string>("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteOpportunityResult | null>(null);
  const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const reload = useCallback(async () => {
    try {
      const d = await loadDetail(opportunityId);
      setDetail(d);
      setPendingStage(d.stage_key);
    } catch {
      setError("Failed to load opportunity");
    } finally {
      setLoading(false);
    }
  }, [loadDetail, opportunityId]);

  useEffect(() => { reload(); }, [reload]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError((typeof detail === "string" ? detail : detail?.message) || "Action failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const rfp = detail?.rfp ?? null;

  if (deleteResult) {
    return (
      <>
      {deleteResult && (
        <ConfirmModal
          title="RFP deleted"
          message={
            <span className="block space-y-2 text-sm">
              <span className="block">
                {deleteResult.title} was deleted with {deleteResult.contact_rows} contact row{deleteResult.contact_rows === 1 ? "" : "s"} and {deleteResult.company_roles} prime link{deleteResult.company_roles === 1 ? "" : "s"}.
              </span>
              <span className="block">
                {deleteResult.companies_deleted.length} prime{deleteResult.companies_deleted.length === 1 ? "" : "s"} with no other RFP {deleteResult.companies_deleted.length === 1 ? "was" : "were"} deleted.
              </span>
              {deleteResult.companies_kept.length > 0 && (
                <span className="block">
                  Stayed in the CRM, now without an RFP:
                  <span className="block mt-1 space-y-0.5 text-muted-foreground">
                    {deleteResult.companies_kept.map((c) => (
                      <span key={c.company_id} className="block">{c.name}: {c.reasons.join("; ")}</span>
                    ))}
                  </span>
                </span>
              )}
            </span>
          }
          confirmLabel="OK"
          cancelLabel="Close"
          onCancel={onClose}
          onConfirm={onClose}
        />
      )}
      </>
    );
  }

  return (
    <>
      {confirmingDelete && detail && deletePreview && (
        <ConfirmModal
          title="Delete RFP permanently"
          size="md"
          message={
            <span className="block space-y-3 text-sm">
              <span className="block">
                This permanently deletes <strong>{deletePreview.title}</strong>, its history and its {deletePreview.company_roles} prime link{deletePreview.company_roles === 1 ? "" : "s"}{deletePreview.conversations ? <> and {deletePreview.conversations} conversation{deletePreview.conversations === 1 ? "" : "s"} ({deletePreview.conversation_messages ?? 0} message{deletePreview.conversation_messages === 1 ? "" : "s"})</> : null}. There is no undo.
              </span>
              {deletePreview.refusal ? (
                <span className="block rounded-lg border border-destructive-border bg-destructive-soft px-3 py-2 text-destructive-soft-foreground">
                  Cannot be deleted: {deletePreview.refusal.message}
                </span>
              ) : (
                <>
                  <span className="block">
                    {deletePreview.companies_deleted.length === 0
                      ? "No prime is left without an RFP, so no prime is deleted."
                      : `${deletePreview.companies_deleted.length} prime${deletePreview.companies_deleted.length === 1 ? "" : "s"} with no other RFP will be deleted too:`}
                  </span>
                  {deletePreview.companies_deleted.length > 0 && (
                    <span className="block max-h-48 overflow-y-auto space-y-1" data-testid="delete-primes">
                      {deletePreview.companies_deleted.map((c) => (
                        <span key={c.company_id} className="block">
                          {c.name}
                          {c.manual.length > 0 && (
                            <span className="block text-xs text-warning-soft-foreground">Has work added by a person, review first: {c.manual.join("; ")}</span>
                          )}
                        </span>
                      ))}
                    </span>
                  )}
                  {deletePreview.companies_kept.length > 0 && (
                    <span className="block">
                      Stay in the CRM, only unlinked:
                      <span className="block mt-1 space-y-0.5 text-muted-foreground">
                        {deletePreview.companies_kept.map((c) => (
                          <span key={c.company_id} className="block">{c.name}: {c.reasons.join("; ")}</span>
                        ))}
                      </span>
                    </span>
                  )}
                </>
              )}
            </span>
          }
          confirmLabel={deletePreview.refusal ? "Close" : "Delete RFP"}
          variant={deletePreview.refusal ? "outline" : "danger"}
          confirming={busy}
          onCancel={() => { setConfirmingDelete(false); setDeletePreview(null); }}
          onConfirm={async () => {
            if (deletePreview.refusal) {
              setConfirmingDelete(false);
              setDeletePreview(null);
              return;
            }
            setBusy(true);
            setError(null);
            try {
              setDeleteResult(await deleteOpportunity(detail.id));
            } catch (err: any) {
              const d = err?.response?.data?.detail;
              setError((typeof d === "string" ? d : d?.message) || "Delete failed. Please try again.");
            } finally {
              setBusy(false);
              setConfirmingDelete(false);
              setDeletePreview(null);
            }
          }}
        />
      )}
      <Modal
        size="lg"
        onClick={onClose}
        closeButtonCLick={onClose}
        modalClassName="max-h-[90vh]"
        icon={<Landmark />}
        title={<span className="break-words">{detail?.title ?? "Loading…"}</span>}
        description={
          detail ? (
            <span className="flex items-center gap-2 flex-wrap">
              <StageBadge stageKey={detail.stage_key} stageName={detail.stage_name} />
              {rfp?.solicitation_number && <span className="font-mono">{rfp.solicitation_number}</span>}
              {rfp?.agency_name && <span>{rfp.agency_name}</span>}
            </span>
          ) : undefined
        }
        bodyClassName="p-6 space-y-6"
      >
        {/* Also wait on pipelineLoaded: reload() (this opportunity) and the
            pipeline fetch (stage options) fire in parallel with no
            ordering guarantee. */}
        {(loading || !pipelineLoaded) && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && pipelineLoaded && detail && (
          <>
            {/* RFP facts + artifacts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <SectionLabel>Solicitation</SectionLabel>
                <div className="bg-surface border border-border rounded-xl px-4 py-1">
                  <FactRow label="Agency">{rfp?.agency_name ?? "—"}</FactRow>
                  <FactRow label="Location">{rfp?.location ?? "—"}</FactRow>
                  <FactRow label="Budget">{rfp?.budget ?? "—"}</FactRow>
                  <FactRow label="NAICS">
                    {rfp?.naics_codes && rfp.naics_codes.length > 0 ? (
                      <span className="inline-flex flex-wrap justify-end gap-1">
                        {rfp.naics_codes.map((code) => (
                          <span
                            key={code}
                            className={`px-1.5 py-0.5 rounded-md text-[11px] font-mono border ${TONE_SOFT.brand}`}
                          >
                            {code}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground" title="Classified automatically by /rfp-primes — not yet run, or predates this RFP's classification">not classified</span>
                    )}
                  </FactRow>
                  <FactRow label="Questions due"><DeadlineChip value={rfp?.questions_deadline} /></FactRow>
                  <FactRow label="Proposal due"><DeadlineChip value={rfp?.proposal_deadline} /></FactRow>
                  <FactRow label="Folder">{rfp?.folder_name ?? "—"}</FactRow>
                </div>
              </div>

              <div>
                <SectionLabel>Artifacts</SectionLabel>
                <div className="bg-surface border border-border rounded-xl px-4 py-1">
                  <FactRow label="Decoded page">
                    {rfp?.page_url ? (
                      <a
                        href={rfp.page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        {rfp.page_slug ?? "view"} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">not built</span>
                    )}
                  </FactRow>
                  <FactRow label="Outreach email">
                    {rfp?.email_built_at ? (
                      <span title={rfp.email_file_path ?? undefined}>built {formatDateTime(rfp.email_built_at)}</span>
                    ) : (
                      <span className="text-muted-foreground">not built</span>
                    )}
                  </FactRow>
                  <FactRow label="Subject line">{rfp?.email_subject ?? "—"}</FactRow>
                  <FactRow label="US Primes CSV">
                    {rfp?.primes_extracted_at ? (
                      <span className="inline-flex items-center gap-2">
                        <span title={rfp.primes_csv_path ?? undefined}>{rfp.primes_row_count ?? "?"} rows</span>
                        <Link
                          to={`${primeContactsBasePath}?opportunity_id=${opportunityId}`}
                          className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                        >
                          <Users className="w-3.5 h-3.5" /> View contacts
                        </Link>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">not extracted</span>
                    )}
                  </FactRow>
                  <FactRow label="Deployed">{rfp?.deployed_at ? formatDateTime(rfp.deployed_at) : <span className="text-muted-foreground">not yet</span>}</FactRow>
                  <FactRow label="Smartlead">
                    {rfp?.outreach_started_at
                      ? `${rfp.smartlead_campaign ?? "campaign"} · ${formatDateTime(rfp.outreach_started_at)}`
                      : <span className="text-muted-foreground">not started</span>}
                  </FactRow>
                  <FactRow label="Engagement">
                    {rfp && (rfp.outreach_replies + rfp.outreach_opens + rfp.outreach_clicks + rfp.outreach_bounces + rfp.outreach_unsubscribes) > 0 ? (
                      <span>
                        <span className={rfp.outreach_replies > 0 ? "text-success-soft-foreground font-medium" : ""}>{rfp.outreach_replies} replies</span>
                        {" · "}{rfp.outreach_opens} opens · {rfp.outreach_clicks} clicks
                        {(rfp.outreach_bounces > 0 || rfp.outreach_unsubscribes > 0) && (
                          <span className="text-muted-foreground"> · {rfp.outreach_bounces} bounced, {rfp.outreach_unsubscribes} unsub</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">no events yet</span>
                    )}
                  </FactRow>
                </div>
              </div>
            </div>

            <GatesSection rfp={rfp} />

            {/* Milestone actions (admin). rfp may be null for manually created
                opportunities — the backend lazily creates the RfpDetail row on
                the first milestone event, so the buttons must still render. */}
            {superAdmin && (
              <div className="flex flex-wrap items-center gap-3">
                {!rfp?.deployed_at && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => run(() => recordRfpEvent(detail.id, "deployed"))}
                    disabled={busy}
                  >
                    <Rocket className="w-4 h-4" />
                    Mark Deployed
                  </Button>
                )}
                {rfp?.deployed_at && !rfp.outreach_started_at && (
                  <div className="flex items-center gap-2">
                    <input
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Smartlead campaign name"
                      className={`px-3 py-1.5 w-56 ${FORM_CONTROL}`}
                    />
                    <input
                      type="date"
                      value={startedOn}
                      max={karachiToday()}
                      onChange={(e) => setStartedOn(e.target.value)}
                      aria-label="Day Start was pressed in SmartLead (blank is today)"
                      title="Day Start was pressed in SmartLead (blank is today). The LinkedIn touches are dated from it."
                      className={`px-2 py-1.5 ${FORM_CONTROL}`}
                    />
                    <Button
                      variant="brand-outline"
                      size="sm"
                      onClick={() => run(() => recordRfpEvent(detail.id, "outreach_started", {
                        ...(campaignName.trim() ? { smartlead_campaign: campaignName.trim() } : {}),
                        ...(startedOn ? { started_on: startedOn } : {}),
                      }))}
                      disabled={busy}
                    >
                      <Send className="w-4 h-4" />
                      Outreach Started
                    </Button>
                  </div>
                )}
              </div>
            )}

            {rfp?.outreach_drafted_at && (
              <div>
                <SectionLabel>Outreach timeline</SectionLabel>
                <RfpOutreachTimelines key={rfp?.outreach_started_at ?? "not-started"} opportunityId={opportunityId} drafted />
              </div>
            )}

            <div>
              {/* Stage control */}
              <div>
                <SectionLabel>Stage</SectionLabel>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={pendingStage}
                    onChange={(e) => setPendingStage(e.target.value)}
                    className={`px-3 py-2 ${FORM_CONTROL}`}
                  >
                    {(pipeline?.stages ?? []).map((s) => (
                      <option key={s.key} value={s.key}>{s.name}</option>
                    ))}
                  </select>
                  <input
                    value={stageNotes}
                    onChange={(e) => setStageNotes(e.target.value)}
                    placeholder="Reason / notes (optional)"
                    className={`flex-1 min-w-32 px-3 py-2 ${FORM_CONTROL}`}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => run(async () => {
                      await changeStage(detail.id, pendingStage, stageNotes.trim() || undefined);
                      setStageNotes("");
                    })}
                    disabled={busy || pendingStage === detail.stage_key}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Stage"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Add note */}
            <div>
              <SectionLabel>Add Note</SectionLabel>
              <div className="flex items-center gap-2">
                <input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="e.g. Prime replied, call booked for Friday"
                  className={`flex-1 px-3 py-2 ${FORM_CONTROL}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && noteText.trim() && !busy) {
                      run(async () => {
                        await addNote(detail.id, noteText.trim());
                        setNoteText("");
                      });
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => run(async () => {
                    await addNote(detail.id, noteText.trim());
                    setNoteText("");
                  })}
                  disabled={busy || !noteText.trim()}
                >
                  <StickyNote className="w-4 h-4" />
                  Add
                </Button>
              </div>
            </div>

            {error && (
              <Alert tone="danger">
                {error}
              </Alert>
            )}

            {/* Activity timeline */}
            <div>
              <SectionLabel>Activity</SectionLabel>
              {detail.activities.length === 0 ? (
                <p className="text-xs text-muted-foreground">No activity yet.</p>
              ) : (
                <ul className="relative ml-1 border-l border-border pl-3">
                  {detail.activities.map((a) => (
                    <ActivityRow key={a.id} activity={a} />
                  ))}
                </ul>
              )}
            </div>

            {superAdmin && (
              <div className="pt-3 border-t border-border flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    setLoadingPreview(true);
                    setError(null);
                    try {
                      setDeletePreview(await getDeletePreview(detail.id));
                      setConfirmingDelete(true);
                    } catch (err: any) {
                      const d = err?.response?.data?.detail;
                      setError((typeof d === "string" ? d : d?.message) || "Could not check what deleting would remove.");
                    } finally {
                      setLoadingPreview(false);
                    }
                  }}
                  disabled={busy || loadingPreview}
                  className="text-xs text-destructive/80 hover:text-destructive hover:underline transition-colors disabled:opacity-40"
                >
                  {loadingPreview ? "Checking…" : "Delete RFP"}
                </button>
              </div>
            )}
          </>
        )}

        {!loading && !detail && (
          <p className="text-sm text-muted-foreground">{error ?? "Not found."}</p>
        )}
      </Modal>
    </>
  );
}
